import React, { useEffect, useState } from 'react';
import { UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../../../components/Button';
import { orderlineDisplayName, type OrderlineView } from '../../../../../../services/orderlines/orderlines.service';
import { formatMoney, formatMoneyCompact } from '../../../../../../utils/money';
import classes from './item-discount-select-modal.module.scss';

export type OrderlineSelection = {
    orderlineId: number;
    quantity: number;
    comboSelections?: import('shared-models/browser').ComboFireSelection[];
};

export type ItemDiscountSelectModalProps = {
    isOpen: boolean;
    title: string;
    confirmLabel?: string;
    showSteps?: boolean;
    orderlines: OrderlineView[];
    isSelectableLine?: (line: OrderlineView) => boolean;
    // Rows stay visible but unselectable when true (e.g. zero-priced lines for non-managers).
    isRowDisabled?: (line: OrderlineView) => boolean;
    // Locked/paid rows: greyed + שולם, non-toggleable, included only on a full selection.
    isLockedIncludedLine?: (line: OrderlineView) => boolean;
    getSelectableQuantity?: (line: OrderlineView) => number;
    resolveSubtotal?: (selections: OrderlineSelection[], orderlines: OrderlineView[]) => number;
    maxSubtotal?: number;
    /** shown under a line that cannot fit within maxSubtotal even at quantity 1 */
    overLimitMessage?: string;
    /**
     * When true, over-limit lines stay selectable and Confirm is enabled once every
     * eligible line (over-limit included) is selected — a full transfer moves the whole
     * order, payment line included, so the maxSubtotal cap no longer applies once nothing
     * is left behind. Used by the transfer flow only; other maxSubtotal consumers (e.g.
     * pay-by-items) must keep the hard block.
     */
    allowFullSelectionOverride?: boolean;
    /** hides the "בחר הכל" (select all) row — used by flows where selecting everything doesn't apply */
    hideSelectAll?: boolean;
    /** hides the header's × close button — used by flows that shouldn't offer a quick dismiss */
    hideCloseButton?: boolean;
    emptyMessage?: string;
    isSubmitting?: boolean;
    allowComboFire?: boolean;
    onClose: () => void;
    onConfirm: (selections: OrderlineSelection[], subtotal: number) => void;
};

const Checkbox = ({ on }: { on: boolean }) => (
    <span className={`${classes.cb} ${on ? classes.cbOn : ''}`} aria-hidden="true">
        {on ? '✓' : ''}
    </span>
);

const ItemDiscountSelectModalComponent: React.FC<ItemDiscountSelectModalProps> = ({
    isOpen,
    title,
    confirmLabel = 'המשך',
    showSteps = true,
    orderlines,
    isSelectableLine,
    isRowDisabled,
    isLockedIncludedLine,
    getSelectableQuantity,
    resolveSubtotal,
    maxSubtotal,
    overLimitMessage,
    allowFullSelectionOverride = false,
    hideSelectAll = false,
    hideCloseButton = false,
    emptyMessage = 'אין פריטים שנשלחו.',
    isSubmitting = false,
    allowComboFire = false,
    onClose,
    onConfirm,
}) => {
    // Map<orderlineId, unitsToDiscount>
    const [sel, setSel] = useState<Map<number, number>>(new Map());
    const [expandedDeals, setExpandedDeals] = useState<Set<number>>(new Set());
    const [comboSel, setComboSel] = useState<Map<number, number>>(new Map());
    // Fully-paid rows selected manually by the user in transfer flow.
    const [selectedPaidLineIds, setSelectedPaidLineIds] = useState<Set<number>>(new Set());
    // over-limit rows are checkable for feedback purposes only - never enter sel/subtotal.
    const [checkedOverLimitIds, setCheckedOverLimitIds] = useState<Set<number>>(new Set());
    // Gesture flag: a locked-only order still needs a בחר הכל press before confirm.
    const [selectAllEngaged, setSelectAllEngaged] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSel(new Map());
            setExpandedDeals(new Set());
            setComboSel(new Map());
            setSelectedPaidLineIds(new Set());
            setCheckedOverLimitIds(new Set());
            setSelectAllEngaged(false);
        }
    }, [isOpen]);

    const getBaseMaxUnits = (line: OrderlineView): number => {
        const fallbackQuantity = Math.max(0, line.quantity);
        if (!getSelectableQuantity) {
            return fallbackQuantity;
        }
        return Math.max(0, Math.min(fallbackQuantity, getSelectableQuantity(line)));
    };

    const shouldIncludeLine = isSelectableLine ?? ((line: OrderlineView) => line.status === 'sent');
    const isLocked = isLockedIncludedLine ?? (() => false);
    const eligibleLines = orderlines.filter((line) => shouldIncludeLine(line) && getBaseMaxUnits(line) > 0);
    // Fully-paid lines (baseMax 0): shown greyed, ride along only on a full selection.
    const paidLines = orderlines.filter((line) => shouldIncludeLine(line) && isLocked(line));

    const resolveSingleLineSubtotal = (line: OrderlineView): number => (
        resolveSubtotal
            ? resolveSubtotal([{ orderlineId: line.id, quantity: 1 }], orderlines)
            : line.unitPrice
    );

    const sentLines = maxSubtotal === undefined
        ? eligibleLines
        : eligibleLines.filter((line) => resolveSingleLineSubtotal(line) <= maxSubtotal + 0.0001);
    const overLimitLines = maxSubtotal === undefined ? [] : eligibleLines.filter((line) => !sentLines.includes(line));
    const sentLineById = new Map(sentLines.map((line) => [line.id, line] as const));

    const getLineDiscountShare = (line: OrderlineView, quantity: number): number => {
        const discountInput = line.discountInput ?? 0;
        return line.quantity > 0
            ? (discountInput * quantity) / line.quantity
            : 0;
    };

    const getLineDisplayAmount = (line: OrderlineView, quantity: number): number => {
        const gross = line.unitPrice * quantity;
        return gross - getLineDiscountShare(line, quantity);
    };

    const resolveSelectionSubtotal = (selectionMap: Map<number, number>): number => {
        const selections: OrderlineSelection[] = sentLines
            .filter((line) => selectionMap.has(line.id))
            .map((line) => ({ orderlineId: line.id, quantity: selectionMap.get(line.id)! }));

        if (resolveSubtotal) {
            return resolveSubtotal(selections, orderlines);
        }

        return sentLines.reduce((sum, line) => {
            if (!selectionMap.has(line.id)) return sum;
            return sum + line.unitPrice * selectionMap.get(line.id)!;
        }, 0);
    };

    const getMaxUnits = (line: OrderlineView, selectionMap: Map<number, number> = sel): number => {
        const baseMaxUnits = getBaseMaxUnits(line);
        if (maxSubtotal === undefined || allowFullSelectionOverride) {
            return baseMaxUnits;
        }

        const nextSelectionMap = new Map(selectionMap);
        nextSelectionMap.delete(line.id);

        let allowedUnits = 0;
        for (let units = 1; units <= baseMaxUnits; units += 1) {
            nextSelectionMap.set(line.id, units);
            if (resolveSelectionSubtotal(nextSelectionMap) <= maxSubtotal + 0.0001) {
                allowedUnits = units;
                continue;
            }
            break;
        }

        return allowedUnits;
    };

    const normalizeSelection = (selectionMap: Map<number, number>): Map<number, number> => {
        const normalized = new Map<number, number>();

        Array.from(selectionMap.entries()).forEach(([lineId, requestedUnits]) => {
            const line = sentLineById.get(lineId);
            if (!line) {
                return;
            }

            const maxUnits = getMaxUnits(line, normalized);
            if (maxUnits <= 0) {
                return;
            }

            normalized.set(lineId, Math.max(1, Math.min(maxUnits, requestedUnits)));
        });

        return normalized;
    };

    const displayLines = sentLines.filter((line) => sel.has(line.id) || getMaxUnits(line) > 0);
    const isEmpty = displayLines.length === 0 && overLimitLines.length === 0 && paidLines.length === 0;

    const isDisabledRow = (line: OrderlineView): boolean => isRowDisabled?.(line) ?? false;

    const toggle = (line: OrderlineView) => {
        if (isSubmitting || isDisabledRow(line)) {
            return;
        }
        const components = allowComboFire ? (line.selections ?? []).filter((item) => item.groupType === 'combo') : [];
        if (expandedDeals.has(line.id) || components.some((item) => comboSel.has(item.selectionId!))) {
            const allSelected = components.every((item) => (comboSel.get(item.selectionId!) ?? 0) === Math.max(0, item.quantity * line.quantity - (item.sentQuantity ?? 0)));
            setComboSel((previous) => {
                const next = new Map(previous);
                for (const item of components) {
                    const pending = Math.max(0, item.quantity * line.quantity - (item.sentQuantity ?? 0));
                    if (allSelected || pending === 0) next.delete(item.selectionId!);
                    else next.set(item.selectionId!, pending);
                }
                return next;
            });
            return;
        }
        setSel((prev) => {
            const next = new Map(prev);
            if (next.has(line.id)) {
                next.delete(line.id);
            } else {
                next.set(line.id, components.length > 0 ? line.quantity : 1);
            }
            return normalizeSelection(next);
        });
    };

    const setUnits = (line: OrderlineView, units: number) => {
        if (isSubmitting) {
            return;
        }
        setSel((prev) => {
            const next = new Map(prev);
            next.set(line.id, units);
            return normalizeSelection(next);
        });
    };

    // Select-all covers enabled rows only; disabled (zero-priced) rows never join.
    const enabledDisplayLines = displayLines.filter((line) => !isDisabledRow(line));
    const allOn = enabledDisplayLines.length > 0 && enabledDisplayLines.every((line) => {
        const maxUnits = getMaxUnits(line);
        return maxUnits > 0 && sel.get(line.id) === maxUnits;
    });

    // Full selection = every eligible line (over-limit ones included) is checked. Only
    // meaningful when allowFullSelectionOverride is set (transfer flow): at that point the
    // maxSubtotal cap is moot because nothing billable is left behind on the source order.
    const allEligibleLines = [...sentLines, ...overLimitLines, ...paidLines];
    const hasSelectableLines = sentLines.length > 0 || overLimitLines.length > 0;
    // Paid lines ride along only on an explicit בחר הכל; manual checks stay a PARTIAL transfer.
    const isFullSelection = allowFullSelectionOverride
        && allEligibleLines.length > 0
        && (paidLines.length === 0 || selectAllEngaged)
        && (hasSelectableLines || selectAllEngaged)
        && overLimitLines.every((line) => checkedOverLimitIds.has(line.id))
        && sentLines.every((line) => sel.get(line.id) === getBaseMaxUnits(line));

    const isEverythingOn = allowFullSelectionOverride ? isFullSelection : allOn;

    // Under the override, getMaxUnits no longer clamps per-line, so the running total can
    // exceed maxSubtotal while a partial (non-full) selection is checked — that's exactly the
    // case that must still show overLimitMessage and block Confirm, per user: "look at the
    // total amount", not each line individually.
    const isOverBudget = allowFullSelectionOverride
        && maxSubtotal !== undefined
        && !isFullSelection
        && resolveSelectionSubtotal(sel) > maxSubtotal + 0.0001;

    const toggleAll = () => {
        if (isSubmitting) {
            return;
        }
        setExpandedDeals(new Set());
        setComboSel(new Map());
        if (isEverythingOn) {
            setSel(new Map());
            setSelectedPaidLineIds(new Set());
            setCheckedOverLimitIds(new Set());
            setSelectAllEngaged(false);
        } else {
            setSel(normalizeSelection(new Map(
                sentLines.filter((line) => !isDisabledRow(line)).map((line) => [line.id, getBaseMaxUnits(line)]),
            )));
            if (allowFullSelectionOverride) {
                setSelectedPaidLineIds(new Set(paidLines.map((line) => line.id)));
                setCheckedOverLimitIds(new Set(overLimitLines.map((line) => line.id)));
                setSelectAllEngaged(true);
            }
        }
    };

    // Locked lines have baseMax 0, so their full-selection quantity is line.quantity.
    const fullSelectionUnits = (line: OrderlineView): number => (isLocked(line) ? line.quantity : getBaseMaxUnits(line));
    const selectedPaidLines = paidLines.filter((line) => selectedPaidLineIds.has(line.id));
    const selections: OrderlineSelection[] = isFullSelection
        ? allEligibleLines.map((line) => ({ orderlineId: line.id, quantity: fullSelectionUnits(line) }))
        : [
            ...sentLines
              .filter((line) => sel.has(line.id))
              .map((line) => ({ orderlineId: line.id, quantity: sel.get(line.id)! })),
            ...selectedPaidLines.map((line) => ({ orderlineId: line.id, quantity: line.quantity })),
          ];
    for (const line of sentLines.filter((item) => (item.selections ?? []).some((component) => comboSel.has(component.selectionId!)))) {
        const comboSelections = (line.selections ?? [])
            .filter((item) => item.groupType === 'combo' && item.selectionId != null && comboSel.has(item.selectionId))
            .map((item) => ({ selectionId: item.selectionId!, quantity: Math.min(comboSel.get(item.selectionId!)!, Math.max(0, item.quantity * line.quantity - (item.sentQuantity ?? 0))) }))
            .filter((item) => item.quantity > 0);
        if (comboSelections.length > 0) selections.push({ orderlineId: line.id, quantity: line.quantity, comboSelections });
    }
    const subtotal = isFullSelection
        ? allEligibleLines.reduce((sum, line) => sum + getLineDisplayAmount(line, fullSelectionUnits(line)), 0)
        : resolveSelectionSubtotal(sel) + selectedPaidLines.reduce((sum, line) => sum + getLineDisplayAmount(line, line.quantity), 0)
            + selections.filter((selection) => selection.comboSelections?.length).reduce((sum, selection) => sum + (sentLineById.get(selection.orderlineId)?.unitPrice ?? 0) * selection.quantity, 0);

    const selectedUnits = isFullSelection
        ? allEligibleLines.reduce((sum, line) => sum + fullSelectionUnits(line), 0)
        : Array.from(sel.values()).reduce((s, u) => s + u, 0) + selectedPaidLines.reduce((sum, line) => sum + line.quantity, 0)
            + Array.from(comboSel.values()).reduce((sum, quantity) => sum + quantity, 0);
    const ready = selections.length > 0 || isFullSelection;

    const handleConfirm = () => {
        if (isSubmitting) {
            return;
        }
        onConfirm(selections, subtotal);
        setSel(new Map());
        setSelectedPaidLineIds(new Set());
        setCheckedOverLimitIds(new Set());
        setSelectAllEngaged(false);
    };

    const handleClose = () => {
        if (isSubmitting) {
            return;
        }
        setSel(new Map());
        setSelectedPaidLineIds(new Set());
        setCheckedOverLimitIds(new Set());
        setSelectAllEngaged(false);
        onClose();
    };

    const onRowKeyDown = (event: React.KeyboardEvent, line: OrderlineView) => {
        if (isSubmitting) {
            return;
        }
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggle(line);
    };

    if (!isOpen) return null;

    return (
        <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={title} dir="rtl">
            <div className={classes.modal}>
                {/* Header */}
                <header className={classes.header}>
                    <span className={classes.title}>{title}</span>
                    {(!hideCloseButton || isEmpty) && (
                        <UnstyledButton
                            type="button"
                            className={classes.closeBtn}
                            onClick={handleClose}
                            aria-label="סגור"
                            disabled={isSubmitting}
                            data-testid="item-discount-select-close"
                        >
                            ×
                        </UnstyledButton>
                    )}
                </header>

                {/* Step indicator */}
                {showSteps && !isEmpty && (
                    <div className={classes.steps}>
                        <span className={`${classes.stepDot} ${classes.stepDotOn}`}>1</span>
                        <span className={`${classes.stepLabel} ${classes.stepLabelOn}`}>בחירת פריטים</span>
                        <span className={classes.stepLine} />
                        <span className={classes.stepDot}>2</span>
                        <span className={classes.stepLabel}>סוג הנחה</span>
                    </div>
                )}

                {!hideSelectAll && !isEmpty && (
                    <UnstyledButton
                        type="button"
                        className={`${classes.row} ${classes.rowAll}`}
                        onClick={toggleAll}
                        disabled={isSubmitting}
                        data-testid="item-discount-select-all"
                    >
                        <span className={classes.itemName}>בחר הכל</span>
                        <span className={classes.spacer} />
                        <Checkbox on={isEverythingOn} />
                    </UnstyledButton>
                )}

                {/* Item list */}
                <div className={classes.list}>
                    {displayLines.length === 0 && overLimitLines.length === 0 && paidLines.length === 0 ? (
                        <p className={classes.empty}>{emptyMessage}</p>
                    ) : (
                        displayLines.map((line) => {
                            const selectableQuantity = getMaxUnits(line);
                            const components = allowComboFire ? (line.selections ?? []).filter((item) => item.groupType === 'combo' && item.selectionId != null) : [];
                            const expanded = expandedDeals.has(line.id);
                            const on = sel.has(line.id) || components.some((item) => comboSel.has(item.selectionId!));
                            const units = sel.get(line.id) ?? selectableQuantity;
                            const multi = selectableQuantity > 1 && components.length === 0;
                            const displayQuantity = on ? units : selectableQuantity;
                            const discountShare = getLineDiscountShare(line, displayQuantity);
                            const grossAmount = line.unitPrice * displayQuantity;
                            const displayAmount = getLineDisplayAmount(line, displayQuantity);
                            const rowDisabled = isDisabledRow(line);

                            return (
                                <React.Fragment key={line.id}>
                                <div
                                    role="button"
                                    tabIndex={isSubmitting || rowDisabled ? -1 : 0}
                                    className={`${classes.row} ${on ? classes.rowOn : ''} ${rowDisabled ? classes.rowDisabled : ''}`}
                                    data-testid={`item-discount-select-row-${line.id}`}
                                    onClick={() => toggle(line)}
                                    onKeyDown={(event) => onRowKeyDown(event, line)}
                                    aria-pressed={on}
                                    aria-disabled={isSubmitting || rowDisabled}
                                >
                                    <span className={classes.qty}>{selectableQuantity}</span>
                                    <span className={classes.itemNameCol}>
                                    <span className={classes.itemName}>{orderlineDisplayName(line)}</span>
                                        {discountShare > 0 && (
                                            <span className={classes.itemDiscountBadge}>
                                                הנחה ₪{formatMoneyCompact(discountShare)} מתוך ₪{formatMoneyCompact(grossAmount)}
                                            </span>
                                        )}
                                    </span>
                                    {on && multi && (
                                        <span
                                            className={classes.stepper}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <UnstyledButton
                                                type="button"
                                                className={classes.rb}
                                                disabled={isSubmitting || units <= 1}
                                                onClick={(e) => { e.stopPropagation(); setUnits(line, units - 1); }}
                                                aria-label="הפחת יחידה"
                                                data-testid={`item-discount-select-dec-${line.id}`}
                                            >
                                                −
                                            </UnstyledButton>
                                            <span className={classes.rbNum}>
                                                <span className={classes.rbNumN}>{units}</span>
                                                <small>מתוך {selectableQuantity}</small>
                                            </span>
                                            <UnstyledButton
                                                type="button"
                                                className={classes.rb}
                                                disabled={isSubmitting || units >= selectableQuantity}
                                                onClick={(e) => { e.stopPropagation(); setUnits(line, units + 1); }}
                                                aria-label="הוסף יחידה"
                                                data-testid={`item-discount-select-inc-${line.id}`}
                                            >
                                                +
                                            </UnstyledButton>
                                        </span>
                                    )}
                                    <span className={classes.itemPrice}>{formatMoney(displayAmount)} ₪</span>
                                    {components.length > 0 && (
                                        <UnstyledButton type="button" className={classes.rb} aria-label={`רכיבי ${orderlineDisplayName(line)}`}
                                            aria-expanded={expanded} disabled={isSubmitting} data-testid={`combo-fire-expand-${line.id}`}
                                            onKeyDown={(event) => event.stopPropagation()}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setExpandedDeals((previous) => { const next = new Set(previous); if (expanded) next.delete(line.id); else next.add(line.id); return next; });
                                                if (!expanded && sel.has(line.id)) {
                                                    setComboSel((previous) => {
                                                        const next = new Map(previous);
                                                        for (const item of components) {
                                                            const pending = Math.max(0, item.quantity * line.quantity - (item.sentQuantity ?? 0));
                                                            if (pending > 0) next.set(item.selectionId!, pending);
                                                        }
                                                        return next;
                                                    });
                                                    setSel((previous) => { const next = new Map(previous); next.delete(line.id); return next; });
                                                }
                                            }}>
                                            <FontAwesomeIcon icon={faChevronDown as IconProp} rotation={expanded ? 180 : undefined} />
                                        </UnstyledButton>
                                    )}
                                    <Checkbox on={on} />
                                </div>
                                {expanded && components.map((item) => {
                                    const sent = item.sentQuantity ?? 0;
                                    const pending = Math.max(0, item.quantity * line.quantity - sent);
                                    const units = comboSel.get(item.selectionId!) ?? 0;
                                    const selectUnits = (quantity: number) => setComboSel((previous) => {
                                        const next = new Map(previous);
                                        if (quantity <= 0) next.delete(item.selectionId!);
                                        else next.set(item.selectionId!, Math.min(pending, quantity));
                                        return next;
                                    });
                                    return <React.Fragment key={item.selectionId}>
                                        {sent > 0 && <div className={`${classes.row} ${classes.rowDisabled}`} data-testid={`combo-fire-sent-${item.selectionId}`}>
                                            <span className={classes.qty}>{sent}</span><span className={classes.itemName}>{item.itemName}</span><span className={classes.spacer} /><span>נשלח</span>
                                        </div>}
                                        {pending > 0 && <div className={`${classes.row} ${units > 0 ? classes.rowOn : ''}`}>
                                            <UnstyledButton type="button" className={classes.itemNameCol} disabled={isSubmitting}
                                                data-testid={`combo-fire-select-${item.selectionId}`} aria-pressed={units > 0}
                                                onClick={() => selectUnits(units > 0 ? 0 : 1)}>
                                                <span className={classes.itemName}>{item.itemName}</span>
                                            </UnstyledButton>
                                            <span className={classes.stepper}>
                                                <UnstyledButton type="button" className={classes.rb} disabled={isSubmitting || units === 0}
                                                    aria-label={`הפחת ${item.itemName}`} onClick={() => selectUnits(units - 1)}>−</UnstyledButton>
                                                <span className={classes.rbNum}>{units}<small>מתוך {pending}</small></span>
                                                <UnstyledButton type="button" className={classes.rb} disabled={isSubmitting || units >= pending}
                                                    aria-label={`הוסף ${item.itemName}`} onClick={() => selectUnits(units + 1)}>+</UnstyledButton>
                                            </span>
                                            <Checkbox on={units > 0} />
                                        </div>}
                                    </React.Fragment>;
                                })}
                                </React.Fragment>
                            );
                        })
                    )}
                    {overLimitLines.map((line) => {
                        const checked = checkedOverLimitIds.has(line.id);
                        const toggleOverLimit = () => {
                            if (isSubmitting) return;
                            setCheckedOverLimitIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(line.id)) {
                                    next.delete(line.id);
                                } else {
                                    next.add(line.id);
                                }
                                return next;
                            });
                        };

                        return (
                            <div
                                key={line.id}
                                role="button"
                                tabIndex={isSubmitting ? -1 : 0}
                                className={`${classes.row} ${checked ? classes.rowOn : ''}`}
                                data-testid={`item-discount-select-over-row-${line.id}`}
                                onClick={toggleOverLimit}
                                onKeyDown={(event) => {
                                    if (isSubmitting || (event.key !== 'Enter' && event.key !== ' ')) return;
                                    event.preventDefault();
                                    toggleOverLimit();
                                }}
                                aria-pressed={checked}
                                aria-disabled={isSubmitting}
                            >
                                <span className={classes.qty}>{line.quantity}</span>
                                <span className={classes.itemNameCol}>
                                    <span className={classes.itemName}>{orderlineDisplayName(line)}</span>
                                </span>
                                <span className={classes.itemPrice}>{formatMoney(line.unitPrice * line.quantity)} ₪</span>
                                <Checkbox on={checked} />
                            </div>
                        );
                    })}
                    {paidLines.map((line) => {
                        const on = selectedPaidLineIds.has(line.id);
                        const togglePaid = () => {
                            if (isSubmitting) {
                                return;
                            }
                            setSelectedPaidLineIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(line.id)) {
                                    next.delete(line.id);
                                } else {
                                    next.add(line.id);
                                }
                                return next;
                            });
                            // Any manual paid-row toggle is no longer a strict select-all gesture.
                            setSelectAllEngaged(false);
                        };

                        return (
                        <div
                            key={line.id}
                            role="button"
                            tabIndex={isSubmitting ? -1 : 0}
                            className={`${classes.row} ${on ? classes.rowOn : ''}`}
                            data-testid={`item-discount-select-paid-row-${line.id}`}
                            onClick={togglePaid}
                            onKeyDown={(event) => {
                                if (isSubmitting || (event.key !== 'Enter' && event.key !== ' ')) return;
                                event.preventDefault();
                                togglePaid();
                            }}
                            aria-pressed={on}
                            aria-disabled={isSubmitting}
                        >
                            <span className={classes.qty}>{line.quantity}</span>
                            <span className={classes.itemNameCol}>
                                <span className={classes.itemName}>{orderlineDisplayName(line)}</span>
                            </span>
                            <span className={classes.paidBadge}>שולם</span>
                            <span className={classes.itemPrice}>{formatMoney(getLineDisplayAmount(line, line.quantity))} ₪</span>
                            <Checkbox on={on} />
                        </div>
                        );
                    })}
                </div>

                {/* Footer */}
                {!isEmpty && (
                    <footer className={classes.footer}>
                        <div className={classes.sumLine}>
                            <span className={classes.sumLabel}>
                                {ready ? `${selectedUnits} פריטים נבחרו` : 'לא נבחרו פריטים'}
                            </span>
                            <span className={`${classes.sum} ${ready ? classes.sumOn : ''}`}>
                                {ready ? `${formatMoney(subtotal)} ₪` : '—'}
                            </span>
                        </div>
                        {((checkedOverLimitIds.size > 0 && !isFullSelection) || isOverBudget) && overLimitMessage && (
                            <p className={classes.overLimitMessage}>{overLimitMessage}</p>
                        )}
                        <div className={classes.btns}>
                            <Button variant="cancel" fullWidth disabled={isSubmitting} onClick={handleClose} dataTestId="item-discount-select-cancel">
                                ביטול
                            </Button>
                            <Button
                                variant="confirm"
                                fullWidth
                                disabled={!ready || isSubmitting || (checkedOverLimitIds.size > 0 && !isFullSelection) || isOverBudget}
                                onClick={handleConfirm}
                                dataTestId="item-discount-select-confirm"
                            >
                                {confirmLabel}
                            </Button>
                        </div>
                    </footer>
                )}
            </div>
        </div>
    );
};

export const ItemDiscountSelectModal = React.memo(ItemDiscountSelectModalComponent);
